import { useState, useEffect, useRef } from 'react'

export const usePolling = (fn, interval = 3000, timeout = 120000) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isPolling, setIsPolling] = useState(false)
  
  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)
  const startTimeRef = useRef(null)

  const startPolling = () => {
    if (isPolling) return

    setIsPolling(true)
    setLoading(true)
    setError(null)
    startTimeRef.current = Date.now()

    const poll = async () => {
      try {
        const result = await fn()
        setData(result)
        setError(null)
        
        // Check if we should stop polling based on result
        if (shouldStopPolling(result)) {
          stopPolling()
          setLoading(false)
          return
        }
        
        // Check for timeout
        const elapsed = Date.now() - startTimeRef.current
        if (elapsed >= timeout) {
          stopPolling()
          setError(new Error('Polling timeout'))
          setLoading(false)
          return
        }
        
      } catch (err) {
        setError(err)
        // Don't stop polling on error, continue trying
      }
    }

    // Initial call
    poll()

    // Set up interval
    intervalRef.current = setInterval(poll, interval)

    // Set up timeout
    timeoutRef.current = setTimeout(() => {
      stopPolling()
      setError(new Error('Polling timeout'))
      setLoading(false)
    }, timeout)
  }

  const stopPolling = () => {
    setIsPolling(false)
    setLoading(false)
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const shouldStopPolling = (result) => {
    // Stop polling for final statuses
    const finalStatuses = ['completed', 'failed', 'expired', 'revoked']
    return result?.status && finalStatuses.includes(result.status)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [])

  // Auto-start polling when function changes
  useEffect(() => {
    if (fn && !isPolling) {
      startPolling()
    }
  }, [fn])

  const restart = () => {
    stopPolling()
    setTimeout(startPolling, 100)
  }

  return {
    data,
    loading,
    error,
    isPolling,
    startPolling,
    stopPolling,
    restart
  }
}

// Specialized hook for redemption status polling
export const useRedemptionPolling = (redemptionId, api) => {
  const pollFunction = redemptionId ? () => api.getRedemptionStatus(redemptionId) : null
  
  return usePolling(pollFunction, 3000, 120000)
}